import React, { useEffect, useState } from "react";
import { getAPIURL } from "../../config";
import userService from "../../services/userService";
import history from "../../history";
import { http } from "../../utils/http";
import { store } from "../../State/store/configureStore";
import {
  LOG_IN_SUCCESS,
  SET_GLOBAL_STORAGE,
  UPDATE_PREFERRED_LOCALE,
} from "../../State/actions/actionTypes";
import { devLog } from "../../utils/devLog";
import Text from "../../components/Text";
import Messages from "../../Shared/Messages";

const EntraIdCallback = () => {
  const [status, setStatus] = useState("processing");
  const [errorKey, setErrorKey] = useState(null);
  const [errorData, setErrorData] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get("sessionId");
      const errorParam = urlParams.get("error");

      if (errorParam) {
        setStatus("error");
        setErrorKey("entra_login_failed_param");
        setErrorData({ error: errorParam });
        setTimeout(() => {
          history.push("/login");
        }, 3000);
        return;
      }

      if (!sessionId) {
        setStatus("error");
        setErrorKey("entra_no_session_id");
        setTimeout(() => {
          history.push("/login");
        }, 3000);
        return;
      }

      try {
        // Exchange session for tokens
        const response = await fetch(
          `${getAPIURL()}/api/auth/entra/exchange-session?sessionId=${sessionId}`,
        );

        if (!response.ok) {
          // Check if the error is specifically about no facility mappings
          const errorBody = await response.json().catch(() => ({}));
          if (
            errorBody.error === "no_facility_mappings" ||
            errorBody.error === "no_matching_groups" ||
            response.status === 403
          ) {
            const userEmail = errorBody.user?.email || errorBody.email || "";
            history.push(
              `/login?entra_fallback=no_mappings&email=${encodeURIComponent(userEmail)}`,
            );
            return;
          }
          throw new Error(errorBody.message || "Failed to exchange session");
        }

        const data = await response.json();

        // Check if user needs to select a facility
        if (data.requiresFacilitySelection) {
          // Store facility selection data in sessionStorage
          sessionStorage.setItem(
            "entraIdFacilitySelection",
            JSON.stringify({
              availableFacilities: data.availableFacilities,
              preAuthToken: data.preAuthToken,
              user: data.user,
            }),
          );

          // Redirect to facility selector
          history.push("/entra-select-facility");
          return;
        }

        // Single facility - proceed with normal login
        // Set the token in HTTP service (must use the same instance as userService)
        http.setToken(data.accessToken);

        // Get space info (updateStore=true to dispatch UPDATE_SPACE_INFO)
        const userInfo = await userService.getSpaceInfo(true);

        // Dispatch LOGIN_SUCCESS to persist authentication in Redux store
        // Must include tokenTimestamp so REHYDRATE can restore the session on refresh
        const userInfoWithoutUIFields = {
          ...userInfo,
          attributeUIFields: undefined,
          authMethod: "entra_id",
        };

        store.dispatch({
          type: LOG_IN_SUCCESS,
          data: userInfoWithoutUIFields,
          token: data.accessToken,
          refreshToken: data.refreshToken,
          expiresIn: data.expiresIn,
          tokenTimestamp: Date.now(),
        });

        // Dispatch global storage and locale — same as normal login saga
        store.dispatch({
          type: SET_GLOBAL_STORAGE,
          data: userInfo.attributeUIFields,
        });

        store.dispatch({
          type: UPDATE_PREFERRED_LOCALE,
          data: {
            preferredLocale:
              localStorage.getItem("preferredLocale") || userInfo.defaultLocale,
          },
        });

        setStatus("success");

        // Small delay to ensure Redux store is updated
        setTimeout(() => {
          // The redux state will determine where to redirect based on role
          history.push("/");
        }, 100);
      } catch (err) {
        devLog("Error exchanging session:", err);
        setStatus("error");
        setErrorKey("entra_login_failed_try_again");
        setTimeout(() => {
          history.push("/login");
        }, 3000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div
      className="animated fadeIn"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "20px",
        textAlign: "center",
      }}
    >
      {status === "processing" && (
        <>
          <i
            className="fa fa-spinner fa-spin"
            style={{ fontSize: "48px", color: "#20a8d8", marginBottom: "20px" }}
          ></i>
          <h3 style={{ color: "#23282c", fontWeight: 500 }}>
            <Text content={Messages.entra_completing_sign_in} />
          </h3>
          <p style={{ color: "#73818f", marginTop: "10px" }}>
            <Text content={Messages.entra_verifying_credentials} />
          </p>
        </>
      )}

      {status === "success" && (
        <>
          <i
            className="fa fa-check-circle"
            style={{ fontSize: "48px", color: "#4dbd74", marginBottom: "20px" }}
          ></i>
          <h3 style={{ color: "#23282c", fontWeight: 500 }}>
            <Text content={Messages.entra_sign_in_successful} />
          </h3>
          <p style={{ color: "#73818f", marginTop: "10px" }}>
            <Text content={Messages.entra_redirecting_to_app} />
          </p>
        </>
      )}

      {status === "error" && (
        <>
          <i
            className="fa fa-exclamation-circle"
            style={{ fontSize: "48px", color: "#f86c6b", marginBottom: "20px" }}
          ></i>
          <h3 style={{ color: "#23282c", fontWeight: 500 }}>
            <Text content={Messages.entra_sign_in_failed} />
          </h3>
          <p style={{ color: "#73818f", marginTop: "10px" }}>
            {errorKey ? (
              <Text content={Messages[errorKey]} data={errorData} />
            ) : (
              <Text content={Messages.entra_error_occurred} />
            )}
          </p>
          <p style={{ color: "#73818f", fontSize: "14px", marginTop: "10px" }}>
            <Text content={Messages.entra_redirecting_to_login} />
          </p>
        </>
      )}
    </div>
  );
};

export default EntraIdCallback;
